import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, List, ListItem, ListItemText,
  Chip, Button, Stack, Divider, CircularProgress
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import VideocamIcon from '@mui/icons-material/Videocam';
import { listTherapistAppointments, listMyAppointments, cancelAppointment } from '../../services/appointmentsApi';
import { createCall } from '../../services/callsApi';
import { useSocket } from '../../context/SocketContext';

function minutesUntil(d) {
  if (!d) return Infinity;
  const ms = new Date(d).getTime() - Date.now();
  return Math.floor(ms / 60000);
}

function formatWhen(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

/**
 * Dashboard widget showing upcoming and recently-past appointments for the
 * caller. Renders for both therapists (own caseload) and clients (their own).
 * When an appointment is within 5 minutes of starting, shows a Join button.
 *
 * `clientIdFilter` (optional): when set, narrows the list to appointments
 * with that client. Used by the per-client dashboard tab so a therapist
 * sees only that client's upcoming sessions.
 */
export default function UpcomingAppointmentsWidget({ token, user, role, clientIdFilter = null, title = 'Upcoming video calls' }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [joining, setJoining] = useState(null);
  const navigate = useNavigate();
  const socket = useSocket();
  // Waiting room (ST-20260915-0005): refresh as soon as a client arrives or leaves.
  // Cancellation (ST-20260917-0006): the server emitted server:appointment:cancelled
  // but nothing in the app listened for it, so a cancelled appointment sat on the
  // therapist's screen until they reloaded. Both directions use the same event.
  useEffect(() => {
    if (!socket) return undefined;
    const refresh = () => setRefreshTick((n) => n + 1);
    socket.on('server:appointment:client_waiting', refresh);
    socket.on('server:appointment:client_left_waiting', refresh);
    socket.on('server:appointment:cancelled', refresh);
    socket.on('server:appointment:updated', refresh);
    return () => {
      socket.off('server:appointment:client_waiting', refresh);
      socket.off('server:appointment:client_left_waiting', refresh);
      socket.off('server:appointment:cancelled', refresh);
      socket.off('server:appointment:updated', refresh);
    };
  }, [socket]);

  const resolvedRole = role
    || (user && Array.isArray(user.role) && user.role.includes('therapist') ? 'therapist'
      : user && Array.isArray(user.role) && user.role.includes('client')    ? 'client'
      : null);
  const isTherapist = resolvedRole === 'therapist';
  const isClient    = resolvedRole === 'client';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = isTherapist
          ? await listTherapistAppointments({ token })
          : isClient
          ? await listMyAppointments({ token })
          : { appointments: [] };
        if (!cancelled) setAppointments(data.appointments || []);
      } catch (err) {
        if (!cancelled) console.warn('[UpcomingAppointmentsWidget] load failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isTherapist, isClient, refreshTick]);

  // Tick once a minute so the Join button appears at the right moment.
  useEffect(() => {
    const t = setInterval(() => setRefreshTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const active = appointments.filter(a => {
    if (!['scheduled', 'confirmed'].includes(a.status)) return false;
    if (new Date(a.scheduledEnd).getTime() <= Date.now() - 30 * 60 * 1000) return false;
    if (clientIdFilter) {
      const apptClientId = a.clientId?._id || a.clientId;
      if (String(apptClientId) !== String(clientIdFilter)) return false;
    }
    return true;
  });

  if (loading || active.length === 0) return null;

  const handleJoin = async (appt) => {
    setJoining(appt._id);
    try {
      if (isTherapist) {
        // Therapist opens (or re-opens) a LiveCall for this appointment.
        const { liveCallId, token: livekitToken } = await createCall({
          token,
          clientId:  appt.clientId?._id || appt.clientId,
          conversationId: appt.conversationId
        });
        navigate(`/call/${liveCallId}`, { state: { livekitToken } });
      } else {
        // Client: the therapist's Start triggers the incoming-call modal,
        // so we don't pre-emptively navigate. Dismiss by refreshing.
        setRefreshTick((n) => n + 1);
      }
    } catch (err) {
      console.error('[UpcomingAppointmentsWidget] join failed:', err.message);
    } finally {
      setJoining(null);
    }
  };

  const handleCancel = async (appt) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await cancelAppointment({ token, appointmentId: appt._id, reason: '' });
      setRefreshTick((n) => n + 1);
    } catch (err) {
      console.error('[UpcomingAppointmentsWidget] cancel failed:', err.message);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <EventIcon fontSize="small" color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{active.length} scheduled</Typography>
      </Box>
      <Divider sx={{ mb: 0.5 }} />
      <List dense disablePadding>
        {active.slice(0, 5).map((appt) => {
          const minsUntil = minutesUntil(appt.scheduledStart);
          const notEnded = new Date(appt.scheduledEnd).getTime() > Date.now();
          const clientWaiting = !!appt.clientWaitingAt && Date.now() - new Date(appt.clientWaitingAt).getTime() < 60 * 1000;
          const waitingRoomOpen = minsUntil <= 10 && notEnded;
          // A waiting client can be seen early, so Start is offered as soon as they arrive.
          const canJoin = (minsUntil <= 5 || clientWaiting) && notEnded;
          const isNow = minsUntil <= 0 && canJoin;
          const other = isTherapist ? appt.clientId : appt.therapistId;
          const otherName = other && typeof other === 'object'
            ? [other.firstName, other.lastName].filter(Boolean).join(' ') || other.email || 'Participant'
            : 'Participant';

          return (
            <ListItem
              key={appt._id}
              sx={{ px: 0 }}
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  {isTherapist && clientWaiting && notEnded && (
                    <Chip size="small" color="success" label="Client waiting" />
                  )}
                  {isTherapist && canJoin && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<VideocamIcon />}
                      disabled={joining === appt._id}
                      onClick={() => handleJoin(appt)}
                    >
                      {isNow ? 'Start now' : 'Start'}
                    </Button>
                  )}
                  {isClient && waitingRoomOpen && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<VideocamIcon />}
                      onClick={() => navigate(`/appointments/${appt._id}/waiting`)}
                    >
                      Join waiting room
                    </Button>
                  )}
                  {isTherapist && !canJoin && (
                    <Button size="small" onClick={() => handleCancel(appt)}>Cancel</Button>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Therapists get a link straight to the client's detail
                        page from the appointment row; clients see plain text.
                        We route via the dashboard's clients tab (rather than
                        the standalone /therapist/client/:id page) because the
                        dashboard owns selectedClient state and renders the
                        full ClientDetailView with all its tabs. */}
                    {isTherapist && (other?._id || (typeof other === 'string' && other)) ? (
                      (() => {
                        const targetId = other._id || other;
                        const target = `/therapist/dashboard?tab=clients&clientId=${targetId}&t=${Date.now()}`;
                        return (
                          <Typography
                            component="a"
                            variant="body2"
                            href={target}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(target);
                            }}
                            sx={{
                              fontWeight: 500,
                              color: 'primary.main',
                              textDecoration: 'none',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {otherName}
                          </Typography>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{otherName}</Typography>
                    )}
                    {appt.status === 'confirmed' && <Chip label="confirmed" size="small" color="success" variant="outlined" sx={{ height: 18 }} />}
                  </Stack>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {formatWhen(appt.scheduledStart)}
                    {minsUntil > 0 && minsUntil < 60 * 24 && ` · in ${minsUntil} min`}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
}
